// @flow

import { call, put, select, takeEvery } from 'redux-saga/effects'
import { createAction, handleActions } from 'redux-actions'
import { messageManager } from 'helpers/MessageManager'
import { formatErrorMessages } from 'frontend_core/dist/api/rest'
import { SET_UI_STATE } from 'redux/modules/ui'
import Cookie from 'js-cookie'
import Path from 'routes/path'
import { callDataLayer } from 'helpers/dataLayer'

type Action = {
  type: string,
  payload: {},
}

type Account = {
  email: string,
  familyName: string,
  givenName: string,
  accountName: string,
  profileImageFile: string,
  password: string,
  passwordConfirmation: string,
  done: string,
  acceptTermOfService: string,
  acceptTermOfGuest: string,
  serviceId: string,
  referralToken: string,
  recaptchaToken: string,
  utm_source?: string,
  utm_medium?: string,
  utm_term?: string,
  utm_campaign?: string,
}

/**
 ************************************************************
 * Actions
 ************************************************************
 **/

const BASE_ACTION = '@@account'
export const actions = {
  setAccount: createAction(`${BASE_ACTION}/SET_ACCOUNT`),
  clearAccount: createAction(`${BASE_ACTION}/CLEAR_ACCOUNT`),
  login: createAction(`${BASE_ACTION}/LOGIN`),
  signUp: createAction(`${BASE_ACTION}/SIGN_UP`),
  signOut: createAction(`${BASE_ACTION}/SIGN_OUT`),
  hostSignUp: createAction(`${BASE_ACTION}/HOST_SIGN_UP`),
  snsSignupAgreement: createAction(`${BASE_ACTION}/SNS_SIGNUP_AGREEMENT`),
}
const successSignUp = createAction(`${BASE_ACTION}/SUCCESS_SIGN_UP`)
const failureSignUp = createAction(`${BASE_ACTION}/FAILURE_SIGN_UP`)

/**
 ************************************************************
 * State
 ************************************************************
 **/

const initialState = {
  email: '',
  password: '',
  passwordConfirmation: '',
  familyName: '',
  givenName: '',
  accountName: '',
  profileImage: null,
  profileImageFile: null,
  rememberMe: true,
  doneUrl: null,
  errorMessage: '',
  isSuccess: false,
  recaptchaToken: '',
  acceptTermOfService: false,
  acceptTermOfGuest: false,
  acceptTermOfHost: false,
  tel: '',
  ownername: '',
}

/**
 ************************************************************
 * Reducer
 ************************************************************
 **/

export const reducer = handleActions(
  {
    [actions.setAccount().type]: (state, action) =>
      Object.assign({}, state, action.payload),
    [actions.clearAccount().type]: state => initialState,
    [failureSignUp().type]: (state, action) =>
      Object.assign({}, state, {
        errors: formatErrorMessages(action.payload.errors),
      }),
    [successSignUp().type]: state =>
      Object.assign({}, state, { isSuccess: true }),
  },
  initialState,
)

/**
 * @description
 * 同意確認のAPIのURLを取得する処理
 *
 * @todo 環境変数で管理へ変更（一旦QA＋リリースに合わせるためにこの実装になっています）
 */
const getAgreementUrl = () => {
  if (
    window.location.host.includes('sandbox') ||
    window.location.host.includes('localhost')
  ) {
    return 'https://api-agreement.spacemarket.dev/agreements/personal-related-data'
  }
  if (window.location.host.includes('staging')) {
    return 'https://api-agreement-staging.spacemarket.com/agreements/personal-related-data'
  }
  return 'https://api-agreement.spacemarket.com/agreements/personal-related-data'
}

/**
 ************************************************************
 * Async Action
 ************************************************************
 **/

// loginのtry catchが長いので、特定のエラーを判別するために使用
class NoAuthorizationError extends Error {
  constructor(message) {
    super(message)
    this.name = 'NoAuthorizationError'
  }
}

function* login(action: {
  payload: { restClient: any, doneUrl?: ?string, serviceId?: ?string },
}) {
  const baseDomain = window.location.host.match(/\.spacemarket\.dev$/)
    ? '.spacemarket.dev'
    : '.spacemarket.com'
  const isProduction =
    ['production', 'sandbox'].indexOf(process.env.NODE_ENV) !== -1
  const secure = isProduction ? true : false
  const domain = isProduction ? baseDomain : null
  const options = { domain, secure }
  if (isProduction) {
    options.sameSite = 'Lax'
  }

  try {
    const { restClient, doneUrl, serviceId } = action.payload
    const state = yield select()
    const account = state.account
    const url = window.location
    const isEmailLogin = !Cookie.get('sm_xct')
    let customToken

    try {
      if (isEmailLogin) {
        const tokenRes = yield call([restClient, 'post'], '1/users/token', {
          username: account.email,
          password: account.password,
        })
        customToken = tokenRes.headers['X-Custom-Token']
      } else {
        customToken = Cookie.get('sm_xct')
      }
    } catch (e) {
      // メール認証が完了していない時。
      if (e?.response?.status === 401) {
        throw new NoAuthorizationError(e.message)
      }
      throw e
    }

    const auth = window.firebase.auth()
    yield call(
      [auth, 'setPersistence'],
      window.firebase.auth.Auth.Persistence.NONE,
    )
    const userCredential = yield call(
      [auth, 'signInWithCustomToken'],
      customToken,
    )
    const user = userCredential.user
    const idTokenResult = yield call([user, 'getIdTokenResult'])
    const { token, issuedAtTime } = idTokenResult
    const refreshToken = userCredential.user.refreshToken
    const csrfToken = Cookie.get('sm_csrf_t')

    const loginRes = yield call(
      [restClient, 'post'],
      `${url.protocol}//${url.host}/api/session`,
      {
        id_token: token,
        refresh_token: refreshToken,
        issued_at: issuedAtTime,
        service_id: 3,
        remember_me: account.rememberMe,
        _csrf: csrfToken,
      },
    )

    if (loginRes.status !== 200) throw new Error('Login Failure')

    restClient.headers['Authorization'] = `Bearer ${loginRes.data.access_token}`
    const userRes = yield call([restClient, 'get'], '1/users/me')
    if (userRes.status !== 200) throw new Error('User Failure')
    Cookie.remove('sm_csrf_t')
    const currentUser = userRes.data
    try {
      const headers = new Headers()
      headers.set('Authorization', `Bearer ${loginRes.data.access_token}`)
      headers.set('Content-Type', 'application/json')
      const agreeRes = yield call(fetch, getAgreementUrl(), {
        method: 'GET',
        headers: headers,
      })
      const resJson = yield call([agreeRes, 'json'])
      // 同意していない場合はCookieを設定しない
      if (resJson.answer == 1) {
        Cookie.set('sm_ag_prd', 1, {
          domain:
            process.env.NODE_ENV === 'production'
              ? '.spacemarket.com'
              : '.spacemarket.dev',
          expires: 365,
        })
      } else if (resJson.answer == 2) {
        Cookie.set('sm_ag_prd', 2, {
          domain:
            process.env.NODE_ENV === 'production'
              ? '.spacemarket.com'
              : '.spacemarket.dev',
          expires: 30,
        })
      }
    } catch (e) {
      // 同意確認のAPIで失敗しても動作影響はないので特に何もしない
    }

    yield callDataLayer({
      event: 'tracking:externalId',
      userId: String(currentUser.id),
    })

    const redirectToSuccessLogin =
      doneUrl &&
      (process.env.NODE_ENV !== 'production' ||
        doneUrl.match(/spacemarket\.(com|dev)/))
        ? decodeURIComponent(doneUrl)
        : state.config.SPM_RENTAL_HOST
    yield put({
      type: actions.setAccount().type,
      payload: { doneUrl: redirectToSuccessLogin },
    })
  } catch (e) {
    Cookie.remove('sm_xct', options)
    console.error(e)
    if (e.name === 'NoAuthorizationError') {
      messageManager.error(
        i18n.t(
          'アカウント承認が完了しておりませんので登録時に使用したメールアドレスをご確認ください。',
        ),
      )
    } else {
      messageManager.error(i18n.t('page.account.l_00033'))
    }
    yield put({
      type: actions.setAccount().type,
      payload: { errorMessage: e.message },
    })
  }
}

function formatHostSignUpFormData(account: Account) {
  const data = new FormData()
  data.append('users[]family_name', account.familyName)
  data.append('users[]given_name', account.givenName)
  data.append('users[]email', account.email)
  data.append('users[]password', account.password)
  data.append('users[]password_confirmation', account.passwordConfirmation)
  data.append('tel', account.tel)
  data.append('done', account.done)
  data.append('service_id', account.serviceId || '')
  // 招待機能一時停止のため、referral_tokenは空文字を送信する
  data.append('referral_token', '')
  data.append('utm_campaign', account.utm_campaign || '')
  data.append('utm_source', account.utm_source || '')
  data.append('utm_medium', account.utm_medium || '')
  data.append('utm_term', account.utm_term || '')
  return data
}

function* hostSignUp(action: {
  payload: {
    restClient: any,
    account: Account,
    options: {
      done: string,
      serviceId: string,
      referralToken: string,
      utm_source?: string,
      utm_medium?: string,
      utm_term?: string,
      utm_campaign?: string,
    },
    currentUser: currentUser,
  },
}) {
  yield put({
    type: SET_UI_STATE,
    payload: { isLoading: true, canSubmit: false },
  })

  const { restClient, account, options, currentUser } = action.payload
  const body = Object.assign({}, account, options)
  const data = formatHostSignUpFormData(body)
  let res
  try {
    if (currentUser) {
      yield put({
        type: SET_UI_STATE,
        payload: { isSubmitFromForm: false },
      })
      res = yield call([restClient, 'post'], '1/owners/')
    } else {
      yield put({
        type: SET_UI_STATE,
        payload: { isSubmitFromForm: true },
      })

      res = yield call([restClient, 'post'], '1/owners/', data)
      // 同意確認のAPIにリクエストを送信
      const token = res.headers['X-Custom-Token']
      const firebaseAuth = window.firebase.auth()
      yield call([firebaseAuth, 'signInWithCustomToken'], token)
      yield call(postAgreement, token, restClient)
    }

    if (res.status !== 201) throw new Error()
    yield put(successSignUp())
    yield put({
      type: actions.setAccount().type,
      payload: { ownername: res.data.username },
    })
  } catch (error) {
    console.error(error)
    res = error.response
    if (res.status !== 400) messageManager.error(error.message)

    // ホスト登録しているユーザーがホストを再登録する場合、warningで表示する
    if (res.status === 400 && currentUser) messageManager.warning(error.message)
    yield put(failureSignUp(res.data))
  }
  yield put({
    type: SET_UI_STATE,
    payload: { isLoading: false, canSubmit: true },
  })
}

function* signUp(action: {
  payload: {
    restClient: any,
    options: { done: string, serviceId: string, referralToken: string },
  },
}) {
  const { restClient, options } = action.payload

  // TODO UIStateはusecaseへ移動
  yield put({
    type: SET_UI_STATE,
    payload: { isLoading: true, canSubmit: false },
  })

  const state = yield select()
  const body = Object.assign({}, state.account, options)
  const data = new FormData()
  data.append('email', body.email)
  data.append('name', body.accountName)
  data.append('profile_image', body.profileImageFile)
  data.append('password', body.password)
  data.append('password_confirmation', body.passwordConfirmation)
  data.append('done', body.done)
  data.append('accept_term_of_service', body.acceptTermOfService)
  data.append('accept_term_of_guest', body.acceptTermOfService)
  data.append('service_id', body.serviceId || '')
  // 招待機能一時停止のため、referral_tokenは空文字を送信する
  data.append('referral_token', '')
  data.append('recaptcha_token', body.recaptchaToken || '')

  // 流入元に関する付加情報
  const paidType = Cookie.get('sm_ad_pt')
  if (paidType) data.append('paid_type', paidType)

  const pageUid = Cookie.get('sm_ad_pi')
  if (pageUid) data.append('page_uid', pageUid)

  const source = Cookie.get('sm_ad_s')
  if (source) data.append('source', source)

  const medium = Cookie.get('sm_ad_m')
  if (medium) data.append('medium', medium)

  const campaign = Cookie.get('sm_ad_c')
  if (campaign) data.append('campaign', campaign)

  const term = Cookie.get('sm_ad_t')
  if (term) data.append('term', term)

  const content = Cookie.get('sm_ad_ct')
  if (content) data.append('utm_content', content)

  try {
    const userRes = yield call([restClient, 'post'], '1/users/', data)
    if (userRes.status !== 201) throw new Error()
    const token = userRes.headers['X-Custom-Token']
    const firebaseAuth = window.firebase.auth()
    yield call([firebaseAuth, 'signInWithCustomToken'], token)
    yield call(postAgreement, token, restClient)
    yield put(successSignUp())
  } catch (e) {
    console.error(e)
    const errorRes = e.response
    if (errorRes.status !== 400) messageManager.error(e.message)
    window.grecaptcha.reset()
    yield put(failureSignUp(errorRes.data))
  }

  Cookie.remove('sm_ad_pt')
  Cookie.remove('sm_ad_pi')
  Cookie.remove('sm_ad_s')
  Cookie.remove('sm_ad_m')
  Cookie.remove('sm_ad_c')
  Cookie.remove('sm_ad_t')
  Cookie.remove('sm_ad_ct')

  // TODO UIStateはusecaseへ移動
  yield put({
    type: SET_UI_STATE,
    payload: { isLoading: false, canSubmit: true },
  })
}

function* signOut(action: {
  payload: {
    restClient: any,
    doneUrl?: string,
  },
}) {
  const { restClient, doneUrl } = action.payload

  try {
    const signOutRes = yield call([restClient, 'delete'], 'i/sign_out')
    if (signOutRes.status !== 204) throw new Error()

    Cookie.remove('sm_at')
    Cookie.remove('sm_rt')
    Cookie.remove('sm_at_ct')
    Cookie.remove('sm_at_et')
    // 同意確認の状態もクリアする
    Cookie.remove('sm_ag_prd')
    Cookie.remove('sm_pics')

    window.location.href = doneUrl || Path.login()
  } catch (error) {
    console.error(error)
  }
}

function* snsSignupAgreement(action) {
  const { payload } = action
  const { token, restClient } = payload
  yield call(postAgreement, token, restClient)
}

/**
 * @description
 * カスタムトークンの値をアクセストークンへ変換し同意確認のAPIへPOSTする処理
 * ※ APIで失敗した場合はサービス側で同意確認のポップアップが出るので気にしない実装する
 *
 * @param {string} customerToken X-Custom-Tokenの値
 * @param {RestClient} restClient
 *
 * @returns {void}
 */
function* postAgreement(customerToken, restClient) {
  try {
    const firebaseAuth = window.firebase.auth()
    const userCredential = yield call(
      [firebaseAuth, 'signInWithCustomToken'],
      customerToken,
    )
    const user = userCredential.user
    const idTokenResult = yield call([user, 'getIdTokenResult'])
    const { token: idToken } = idTokenResult
    const url = window.location
    const tokenResponse = yield call(
      [restClient, 'post'],
      `${url.protocol}//${url.host}/api/session/agreement-token`,
      {
        id_token: idToken,
      },
    )
    const headers = new Headers()
    const auth = `Bearer ${tokenResponse.data.access_token}`
    headers.set('Authorization', auth)
    headers.set('Content-Type', 'application/json')
    yield call(fetch, getAgreementUrl(), {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        answer: 1,
      }),
    })
  } catch (e) {
    console.error('ACCOUNT_SIGNUP_AGREEMENT_ERROR: ', e.message)
  }
}

export const sagas = [
  takeEvery(actions.login().type, login),
  takeEvery(actions.signUp().type, signUp),
  takeEvery(actions.hostSignUp().type, hostSignUp),
  takeEvery(actions.signOut().type, signOut),
  takeEvery(actions.snsSignupAgreement().type, snsSignupAgreement),
]
